import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { X, RotateCcw, Undo2, XCircle } from 'lucide-react';
import api from '@/lib/api';

// Consumer-side modal for creating a Cancel / Exchange / Return request.
// Type is pre-selected. Sub-order pre-selected. Reason + note + confirm.
//
// Props: {
//   open, onClose, onCreated,
//   type: 'cancel' | 'exchange' | 'return',
//   orderId, orderNumber,
//   subOrder: { seller_order_id, seller_id, seller_name_snapshot, items[], status },
// }

const REASONS = {
  cancel: [
    { k: 'buyer_change_of_mind', label: '단순 변심 (마음이 바뀌었어요)' },
    { k: 'product_defect', label: '상품 정보/가격 오류' },
    { k: 'wrong_delivery', label: '중복 주문' },
    { k: 'other', label: '기타' },
  ],
  return: [
    { k: 'buyer_change_of_mind', label: '단순 변심' },
    { k: 'product_defect', label: '상품 불량' },
    { k: 'wrong_delivery', label: '오배송' },
    { k: 'damaged', label: '파손 · 하자' },
    { k: 'other', label: '기타' },
  ],
  exchange: [
    { k: 'product_defect', label: '상품 불량' },
    { k: 'wrong_delivery', label: '다른 상품 배송' },
    { k: 'damaged', label: '파손 · 하자' },
    { k: 'buyer_change_of_mind', label: '옵션 변경' },
    { k: 'other', label: '기타' },
  ],
};

const TITLE = { cancel: '주문 취소 요청', exchange: '교환 신청', return: '반품 신청' };
const ICON = { cancel: XCircle, exchange: RotateCcw, return: Undo2 };
const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

export default function BuyerRmaRequestModal({ open, onClose, onCreated, type, orderId, orderNumber, subOrder }) {
  const [reasonCode, setReasonCode] = useState('');
  const [reasonNote, setReasonNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { if (open) { setReasonCode(''); setReasonNote(''); } }, [open, type]);
  if (!open) return null;

  const Icon = ICON[type] || XCircle;
  const reasons = REASONS[type] || [];
  const canSubmit = !!reasonCode && !submitting;

  const submit = async () => {
    if (!canSubmit) { toast.error('사유를 선택해주세요.'); return; }
    setSubmitting(true);
    try {
      const r = await api.post(`/orders/${orderId}/rma`, {
        type,
        seller_order_id: subOrder?.seller_order_id,
        reason_code: reasonCode,
        reason_note: reasonNote.trim() || undefined,
      });
      toast.success(`${TITLE[type]}이 접수되었습니다.`);
      onCreated?.(r.data);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '요청 실패');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" data-testid="buyer-rma-modal">
      <div className="w-full sm:max-w-md bg-white dark:bg-[#141414] rounded-t-2xl sm:rounded-2xl p-5 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Icon className="text-[#FF3B30]" size={20} />
            <p className="text-lg font-black">{TITLE[type]}</p>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-black/5 dark:hover:bg-white/10" data-testid="buyer-rma-close"><X size={16}/></button>
        </div>

        <div className="rounded-xl border border-black/10 dark:border-white/10 p-3 mb-4 text-xs space-y-1">
          <p><span className="text-gray-500">주문번호:</span> <span className="font-bold">{orderNumber}</span></p>
          <p><span className="text-gray-500">판매자:</span> <span className="font-bold">{subOrder?.seller_name_snapshot || '-'}</span></p>
          <p><span className="text-gray-500">상품:</span> {(subOrder?.items || []).map((it) => `${it.name_snapshot || it.product_name} × ${it.quantity}`).join(', ')}</p>
        </div>

        <div className="mb-4">
          <p className="text-xs font-black mb-2">사유 <span className="text-[#FF3B30]">*</span></p>
          <div className="space-y-1.5" data-testid="buyer-rma-reason-list">
            {reasons.map((r) => (
              <label key={r.k} className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-xs ${reasonCode === r.k ? 'border-[#FF3B30] bg-[#FF3B30]/5' : 'border-black/10 dark:border-white/10'}`}>
                <input type="radio" name="rma-reason" value={r.k} checked={reasonCode === r.k}
                  onChange={() => setReasonCode(r.k)} data-testid={`buyer-rma-reason-${r.k}`} />
                <span>{r.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="mb-4">
          <p className="text-xs font-black mb-2">상세 설명 (선택)</p>
          <textarea rows={3} value={reasonNote} onChange={(e) => setReasonNote(e.target.value)}
            placeholder={type === 'exchange' ? '교환을 원하시는 옵션을 알려주세요.' : '상세 사유를 입력해주세요.'}
            className="w-full px-3 py-2 text-xs rounded-lg border border-black/10 dark:border-white/10 bg-transparent"
            data-testid="buyer-rma-note" />
        </div>

        {type === 'cancel' && (
          <p className="text-[11px] text-amber-700 bg-amber-50 dark:bg-amber-500/10 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30 rounded-lg px-2 py-1.5 mb-4">
            결제완료 주문은 판매자 확인 후 취소가 진행됩니다. (자동 취소되지 않음)
          </p>
        )}
        {type !== 'cancel' && (
          <p className="text-[11px] text-amber-700 bg-amber-50 dark:bg-amber-500/10 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30 rounded-lg px-2 py-1.5 mb-4">
            판매자 승인 후 회수 절차가 진행됩니다. TEST MODE · 실제 회수 API 미연결.
          </p>
        )}

        <div className="flex gap-2">
          <button onClick={onClose} className="flex-1 py-3 rounded-full border border-black/10 dark:border-white/10 text-xs font-black">취소</button>
          <button onClick={submit} disabled={!canSubmit} className="flex-1 py-3 rounded-full bg-[#FF3B30] text-white text-xs font-black disabled:opacity-40" data-testid="buyer-rma-submit">
            {submitting ? '접수 중…' : '요청 접수'}
          </button>
        </div>
      </div>
    </div>
  );
}
